import React, { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PackageStatusBadge } from 'components/common/StatusBadge';
import { Money } from 'components/common/utils';
import { cn } from 'utils';

import styles from '../AccountDetails.module.scss';
import { type ComfortPackageRow } from './comfortPackageRows';

const INITIAL_PACKAGES_COUNT = 5;

type Props = {
  rows: ComfortPackageRow[];
  emptyMessage: string;
};

export const ComfortPackagesTable: FC<Props> = ({ rows, emptyMessage }) => {
  const navigate = useNavigate();
  const [showAllPackages, setShowAllPackages] = useState(false);

  const visibleRows = useMemo(() => {
    if (showAllPackages) {
      return rows;
    }

    return rows.slice(0, INITIAL_PACKAGES_COUNT);
  }, [rows, showAllPackages]);

  const hasMorePackages = rows.length > INITIAL_PACKAGES_COUNT && !showAllPackages;

  return (
    <>
      <div className={styles.tableCard}>
        <div className={cn(styles.tableHeader, styles.comfortTableHeader)}>
          <div className={styles.th}>ПАКЕТ</div>
          <div className={styles.th}>СТАТУС</div>
          <div className={styles.th}>ДАТА ВХОДА</div>
          <div className={styles.th}>ИНВЕСТИРОВАНО</div>
          <div className={styles.th}>ВОЗВРАЩЕНО</div>
          <div className={styles.th}>ПРОГНОЗ ВОЗВРАТА</div>
          <div className={styles.th}>ROI / IRR</div>
        </div>

        <div className={styles.tableBody}>
          {rows.length === 0 ? (
            <div className={styles.tableEmpty}>{emptyMessage}</div>
          ) : (
            visibleRows.map((row) => (
              <button
                key={row.key}
                type="button"
                className={cn(styles.tableRow, styles.comfortTableRow, styles.clickableTableRow)}
                onClick={() => navigate(`package/${row.key}`, { relative: 'path' })}
              >
                <div className={cn(styles.td, styles.tdProject)}>
                  <div className={styles.projectName}>{row.packageTitle}</div>
                  <div className={styles.projectId}>ID {row.packageId}</div>
                </div>
                <div className={cn(styles.td, styles.tdStatus)} data-label="Статус">
                  <PackageStatusBadge status={row.packageStatus} />
                </div>
                <div className={styles.td} data-label="Дата входа">
                  {row.entryDate}
                </div>
                <div className={styles.td} data-label="Инвестировано">
                  <Money money={row.invested} />
                </div>
                <div className={styles.td} data-label="Возвращено">
                  {row.returned ? <Money money={row.returned} /> : '—'}
                </div>
                <div className={styles.td} data-label="Прогноз возврата">
                  {row.forecastReturn ? <Money money={row.forecastReturn} /> : '—'}
                </div>
                <div className={styles.td} data-label="ROI / IRR">
                  {row.roiIrr ?? '—'}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {hasMorePackages ? (
        <button
          type="button"
          className={styles.showAllButton}
          onClick={() => setShowAllPackages(true)}
        >
          Показать все пакеты
        </button>
      ) : null}
    </>
  );
};
